/**
 * ✅ FERP SAMPLE TASK TEMPLATES - Metadata DSL v1.0
 *
 * 4 production-ready Task templates demonstrating Metadata DSL integration:
 * 1. Patient Transfer Task (Healthcare - HIPAA)
 * 2. Financial Audit Task (Finance - SOX)
 * 3. Legal Discovery Task (Legal - Attorney-Client Privilege)
 * 4. Emergency Response Task (Life-Critical)
 *
 * ARCHITECTURE COMPLIANCE:
 * - ✅ Data-driven: All values from template metadata
 * - ✅ 16 primitives: Task is primitive #11
 * - ✅ Complete DSL structure: business, UI, primitives, mappings, compliance
 * - ✅ Life-critical safety: Complete audit trail integration
 * - ✅ Domain-agnostic: Templates work across all domains
 */

import type { WorkflowTemplateMetadata } from '../../hooks/useWorkflowTemplate';

/**
 * Template 1: Patient Transfer Task (Healthcare - HIPAA Compliant)
 *
 * Features:
 * - Life-critical flag for patient safety
 * - Clinical role requirements (RN, MD)
 * - PHI confidentiality
 * - Evidence tracking required
 * - HIPAA + Joint Commission compliance
 */
export const PATIENT_TRANSFER_TASK_TEMPLATE: WorkflowTemplateMetadata = {
  templateId: 'task-patient-transfer',
  templateVersion: '1.0.0',
  schemaVersion: '1.0',
  tenant: 'demo-tenant',
  domain: 'healthcare',
  templateType: 'Task',
  createdBy: 'system',
  updatedBy: 'system',
  versionHistory: [],
  usageCount: 0,

  metadata: {
    business: {
      name: 'Patient Transfer Task',
      domain: 'healthcare',
      category: 'Clinical Workflow',
      description: 'Comprehensive task for patient transfers between units with HIPAA compliance',
      icon: 'IconAmbulance',
      tags: ['patient-safety', 'hipaa', 'clinical', 'transfer'],
    },

    ui: {
      layout: 'tabs',
      steps: [
        {
          id: 'step_basic',
          label: 'Basic Information',
          fields: [
            {
              id: 'name',
              component: 'text',
              label: 'Task Name',
              required: true,
              helpText: 'Short descriptive name for the transfer task',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'name' }],
            },
            {
              id: 'description',
              component: 'textarea',
              label: 'Description',
              required: true,
              helpText: 'Detailed description of the patient transfer',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'description' }],
            },
            {
              id: 'priority',
              component: 'select',
              label: 'Priority',
              required: true,
              helpText: 'Priority level for the transfer',
              dataSource: {
                type: 'static',
                staticOptions: ['low', 'medium', 'high', 'urgent'],
              },
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'priority' }],
            },
            {
              id: 'isCritical',
              component: 'checkbox',
              label: 'Life Critical',
              required: false,
              helpText: 'Mark as life-critical patient transfer',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'is_critical' }],
            },
          ],
        },
        {
          id: 'step_assignment',
          label: 'Assignment',
          fields: [
            {
              id: 'assignedRole',
              component: 'select',
              label: 'Assigned Role',
              required: true,
              helpText: 'Clinical role responsible for transfer',
              dataSource: {
                type: 'static',
                staticOptions: ['RN', 'MD', 'Paramedic', 'Transfer Coordinator'],
              },
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'assigned_role' }],
            },
            {
              id: 'watchers',
              component: 'tags',
              label: 'Watchers (User IDs)',
              required: false,
              helpText: 'Clinical staff to notify about transfer',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'watchers' }],
            },
          ],
        },
        {
          id: 'step_scheduling',
          label: 'Scheduling',
          fields: [
            {
              id: 'dueDate',
              component: 'datetime',
              label: 'Transfer Due Date',
              required: true,
              helpText: 'When the transfer should be completed',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'due_date' }],
            },
            {
              id: 'estimatedDuration',
              component: 'number',
              label: 'Estimated Duration (minutes)',
              required: false,
              helpText: 'Expected transfer duration',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'estimated_duration_minutes' }],
            },
          ],
        },
        {
          id: 'step_compliance',
          label: 'Compliance',
          fields: [
            {
              id: 'complianceFrameworks',
              component: 'multiselect',
              label: 'Compliance Frameworks',
              required: true,
              helpText: 'Applicable regulatory frameworks',
              dataSource: {
                type: 'static',
                staticOptions: ['HIPAA', 'Joint Commission', 'CMS'],
              },
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'compliance_frameworks' }],
            },
            {
              id: 'isConfidential',
              component: 'checkbox',
              label: 'Contains PHI',
              required: false,
              helpText: 'Task involves Protected Health Information',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'is_confidential' }],
            },
            {
              id: 'evidenceRequired',
              component: 'checkbox',
              label: 'Evidence Required',
              required: false,
              helpText: 'Requires evidence collection for compliance',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'evidence_required' }],
            },
          ],
        },
      ],
    },

    primitives: {
      nodes: [
        {
          id: 'task_main',
          type: 'Task',
          name: 'Patient Transfer Task',
          metadata: {
            task_type: 'manual',
            domain: 'patient_safety',
            life_critical: true,
            hipaa_compliant: true,
          },
        },
      ],
      connections: [],
    },

    mappings: {}, // Field mappings stored in mapsTo properties

    compliance: {
      frameworks: ['HIPAA', 'Joint Commission'],
      auditLevel: 'complete',
    },
  },

  createdAt: new Date('2025-10-09').toISOString(),
  updatedAt: new Date('2025-10-09').toISOString(),
};

/**
 * Template 2: Financial Audit Task (Finance - SOX Compliant)
 *
 * Features:
 * - Dual control (two-person rule)
 * - MFA requirements
 * - Audit evidence collection
 * - 7-year retention (2555 days)
 * - SOX + GAAP compliance
 */
export const FINANCIAL_AUDIT_TASK_TEMPLATE: WorkflowTemplateMetadata = {
  templateId: 'task-financial-audit',
  templateVersion: '1.0.0',
  schemaVersion: '1.0',
  tenant: 'demo-tenant',
  domain: 'finance',
  templateType: 'Task',
  createdBy: 'system',
  updatedBy: 'system',
  versionHistory: [],
  usageCount: 0,

  metadata: {
    business: {
      name: 'Financial Audit Task',
      domain: 'finance',
      category: 'Audit & Compliance',
      description: 'Financial audit task with dual control and SOX compliance',
      icon: 'IconReportMoney',
      tags: ['audit', 'sox', 'finance', 'compliance'],
    },

    ui: {
      layout: 'tabs',
      steps: [
        {
          id: 'step_basic',
          label: 'Basic Information',
          fields: [
            {
              id: 'name',
              component: 'text',
              label: 'Audit Task Name',
              required: true,
              helpText: 'Name of the audit task',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'name' }],
            },
            {
              id: 'description',
              component: 'textarea',
              label: 'Description',
              required: true,
              helpText: 'Detailed description of audit scope',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'description' }],
            },
            {
              id: 'priority',
              component: 'select',
              label: 'Priority',
              required: true,
              helpText: 'Audit priority level',
              dataSource: {
                type: 'static',
                staticOptions: ['low', 'medium', 'high', 'urgent'],
              },
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'priority' }],
            },
          ],
        },
        {
          id: 'step_approval',
          label: 'Approval & Dual Control',
          fields: [
            {
              id: 'dualControlRequired',
              component: 'checkbox',
              label: 'Dual Control Required',
              required: false,
              helpText: 'Requires two-person approval (SOX requirement)',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'dual_control_required' }],
            },
            {
              id: 'requiresMfa',
              component: 'checkbox',
              label: 'Requires MFA',
              required: false,
              helpText: 'Multi-factor authentication required',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'requires_mfa' }],
            },
            {
              id: 'approvers',
              component: 'tags',
              label: 'Approvers (User IDs)',
              required: true,
              helpText: 'User IDs who can approve this audit',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'approvers' }],
            },
          ],
        },
        {
          id: 'step_compliance',
          label: 'Compliance',
          fields: [
            {
              id: 'complianceFrameworks',
              component: 'multiselect',
              label: 'Compliance Frameworks',
              required: true,
              helpText: 'Applicable regulatory frameworks',
              dataSource: {
                type: 'static',
                staticOptions: ['SOX', 'GAAP', 'IRS', 'SEC'],
              },
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'compliance_frameworks' }],
            },
            {
              id: 'evidenceRequired',
              component: 'checkbox',
              label: 'Evidence Required',
              required: false,
              helpText: 'Requires audit evidence collection',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'evidence_required' }],
            },
            {
              id: 'retentionPeriodDays',
              component: 'number',
              label: 'Retention Period (days)',
              required: true,
              helpText: '7-year retention for SOX compliance (2555 days)',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'retention_period_days' }],
            },
          ],
        },
      ],
    },

    primitives: {
      nodes: [
        {
          id: 'task_main',
          type: 'Task',
          name: 'Financial Audit Task',
          metadata: {
            task_type: 'approval',
            domain: 'financial_audit',
            sox_compliant: true,
            dual_control: true,
          },
        },
      ],
      connections: [],
    },

    mappings: {}, // Field mappings stored in mapsTo properties

    compliance: {
      frameworks: ['SOX', 'GAAP'],
      auditLevel: 'complete',
    },
  },

  createdAt: new Date('2025-10-09').toISOString(),
  updatedAt: new Date('2025-10-09').toISOString(),
};

/**
 * Template 3: Legal Discovery Task (Legal - Attorney-Client Privilege)
 *
 * Features:
 * - Attorney-client privilege protection
 * - Legal hold support
 * - Confidential information handling
 * - Case number tracking
 * - FRCP compliance
 */
export const LEGAL_DISCOVERY_TASK_TEMPLATE: WorkflowTemplateMetadata = {
  templateId: 'task-legal-discovery',
  templateVersion: '1.0.0',
  schemaVersion: '1.0',
  tenant: 'demo-tenant',
  domain: 'legal',
  templateType: 'Task',
  createdBy: 'system',
  updatedBy: 'system',
  versionHistory: [],
  usageCount: 0,

  metadata: {
    business: {
      name: 'Legal Discovery Task',
      domain: 'legal',
      category: 'Legal & Compliance',
      description: 'Legal discovery task with privilege protection and FRCP compliance',
      icon: 'IconScale',
      tags: ['legal', 'discovery', 'privilege', 'frcp'],
    },

    ui: {
      layout: 'tabs',
      steps: [
        {
          id: 'step_basic',
          label: 'Basic Information',
          fields: [
            {
              id: 'name',
              component: 'text',
              label: 'Discovery Task Name',
              required: true,
              helpText: 'Name of the discovery task',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'name' }],
            },
            {
              id: 'description',
              component: 'textarea',
              label: 'Description',
              required: true,
              helpText: 'Description of discovery scope',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'description' }],
            },
            {
              id: 'tags',
              component: 'tags',
              label: 'Case Number',
              required: false,
              helpText: 'Case number(s) for tracking',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'tags' }],
            },
          ],
        },
        {
          id: 'step_assignment',
          label: 'Assignment',
          fields: [
            {
              id: 'assignedRole',
              component: 'select',
              label: 'Assigned Role',
              required: true,
              helpText: 'Legal role responsible for discovery',
              dataSource: {
                type: 'static',
                staticOptions: ['Attorney', 'Paralegal', 'Legal Secretary'],
              },
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'assigned_role' }],
            },
          ],
        },
        {
          id: 'step_legal_compliance',
          label: 'Legal Compliance',
          fields: [
            {
              id: 'isPrivileged',
              component: 'checkbox',
              label: 'Attorney-Client Privilege',
              required: false,
              helpText: 'Protected by attorney-client privilege',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'is_privileged' }],
            },
            {
              id: 'legalHold',
              component: 'checkbox',
              label: 'Legal Hold',
              required: false,
              helpText: 'Subject to legal hold (cannot be deleted)',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'legal_hold' }],
            },
            {
              id: 'isConfidential',
              component: 'checkbox',
              label: 'Confidential',
              required: false,
              helpText: 'Contains confidential legal information',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'is_confidential' }],
            },
            {
              id: 'complianceFrameworks',
              component: 'multiselect',
              label: 'Compliance Frameworks',
              required: true,
              helpText: 'Applicable legal frameworks',
              dataSource: {
                type: 'static',
                staticOptions: ['FRCP', 'Attorney-Client Privilege', 'Work Product Doctrine'],
              },
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'compliance_frameworks' }],
            },
          ],
        },
      ],
    },

    primitives: {
      nodes: [
        {
          id: 'task_main',
          type: 'Task',
          name: 'Legal Discovery Task',
          metadata: {
            task_type: 'manual',
            domain: 'legal_discovery',
            privileged: true,
            legal_hold: true,
          },
        },
      ],
      connections: [],
    },

    mappings: {}, // Field mappings stored in mapsTo properties

    compliance: {
      frameworks: ['Attorney-Client Privilege', 'FRCP'],
      auditLevel: 'complete',
    },
  },

  createdAt: new Date('2025-10-09').toISOString(),
  updatedAt: new Date('2025-10-09').toISOString(),
};

/**
 * Template 4: Emergency Response Task (Life-Critical)
 *
 * Features:
 * - Life-critical + immediate attention flags
 * - SLA deadline enforcement
 * - Escalation chain
 * - Multi-channel notifications (email, SMS, pager)
 * - 5-minute SLA buffer
 */
export const EMERGENCY_RESPONSE_TASK_TEMPLATE: WorkflowTemplateMetadata = {
  templateId: 'task-emergency-response',
  templateVersion: '1.0.0',
  schemaVersion: '1.0',
  tenant: 'demo-tenant',
  domain: 'healthcare',
  templateType: 'Task',
  createdBy: 'system',
  updatedBy: 'system',
  versionHistory: [],
  usageCount: 0,

  metadata: {
    business: {
      name: 'Emergency Response Task',
      domain: 'healthcare',
      category: 'Emergency Management',
      description: 'Life-critical emergency response task with SLA enforcement',
      icon: 'IconAlertTriangle',
      tags: ['emergency', 'life-critical', 'urgent', 'sla'],
    },

    ui: {
      layout: 'tabs',
      steps: [
        {
          id: 'step_emergency',
          label: 'Emergency Details',
          fields: [
            {
              id: 'name',
              component: 'text',
              label: 'Emergency Task Name',
              required: true,
              helpText: 'Name of the emergency response task',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'name' }],
            },
            {
              id: 'description',
              component: 'textarea',
              label: 'Description',
              required: true,
              helpText: 'Detailed description of the emergency',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'description' }],
            },
            {
              id: 'isCritical',
              component: 'checkbox',
              label: 'Life Critical',
              required: false,
              helpText: 'This is a life-critical emergency',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'is_critical' }],
            },
            {
              id: 'requiresImmediateAttention',
              component: 'checkbox',
              label: 'Immediate Attention Required',
              required: false,
              helpText: 'Requires immediate attention',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'requires_immediate_attention' }],
            },
          ],
        },
        {
          id: 'step_escalation',
          label: 'Escalation & SLA',
          fields: [
            {
              id: 'slaDeadline',
              component: 'datetime',
              label: 'SLA Deadline',
              required: true,
              helpText: 'Service-level agreement deadline',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'sla_deadline' }],
            },
            {
              id: 'slaBufferMinutes',
              component: 'number',
              label: 'SLA Buffer (minutes)',
              required: false,
              helpText: 'Buffer time before SLA breach (default: 5 minutes)',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'sla_buffer_minutes' }],
            },
            {
              id: 'escalationChain',
              component: 'tags',
              label: 'Escalation Chain (User IDs)',
              required: true,
              helpText: 'Ordered list of user IDs for escalation',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'escalation_chain' }],
            },
            {
              id: 'notificationChannels',
              component: 'multiselect',
              label: 'Notification Channels',
              required: true,
              helpText: 'Channels for emergency notifications',
              dataSource: {
                type: 'static',
                staticOptions: ['email', 'sms', 'pager', 'slack', 'phone'],
              },
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'notification_channels' }],
            },
          ],
        },
        {
          id: 'step_compliance',
          label: 'Compliance',
          fields: [
            {
              id: 'complianceFrameworks',
              component: 'multiselect',
              label: 'Compliance Frameworks',
              required: true,
              helpText: 'Applicable regulatory frameworks',
              dataSource: {
                type: 'static',
                staticOptions: ['HIPAA', 'EMTALA', 'Joint Commission'],
              },
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'compliance_frameworks' }],
            },
            {
              id: 'evidenceRequired',
              component: 'checkbox',
              label: 'Evidence Required',
              required: false,
              helpText: 'Requires evidence collection',
              mapsTo: [{ primitiveType: 'Task', primitiveId: 'task_main', fieldPath: 'evidence_required' }],
            },
          ],
        },
      ],
    },

    primitives: {
      nodes: [
        {
          id: 'task_main',
          type: 'Task',
          name: 'Emergency Response Task',
          metadata: {
            task_type: 'manual',
            domain: 'emergency_response',
            life_critical: true,
            immediate_attention: true,
          },
        },
      ],
      connections: [],
    },

    mappings: {}, // Field mappings stored in mapsTo properties

    compliance: {
      frameworks: ['HIPAA', 'EMTALA'],
      auditLevel: 'complete',
    },
  },

  createdAt: new Date('2025-10-09').toISOString(),
  updatedAt: new Date('2025-10-09').toISOString(),
};

/**
 * Export all sample Task templates
 * These templates are merged with backend templates in useWorkflowTemplate hook
 */
export const SAMPLE_TASK_TEMPLATES: WorkflowTemplateMetadata[] = [
  PATIENT_TRANSFER_TASK_TEMPLATE,
  FINANCIAL_AUDIT_TASK_TEMPLATE,
  LEGAL_DISCOVERY_TASK_TEMPLATE,
  EMERGENCY_RESPONSE_TASK_TEMPLATE,
];
